import { GetFileSearchParams, HybridSearchBody } from './schemas';

/**
 * v2 신규/확장 API 쿼리 키 팩토리.
 * 기존 v2 훅들이 쓰는 인라인 `['v2', ...]` 키와 prefix 체계를 공유한다
 * (뮤테이션의 prefix 무효화가 신·구 쿼리에 함께 적용되도록).
 */
export const v2QueryKeys = {
  all: ['v2'] as const,

  /**
   * 폴더 트리 — 키 앞부분은 기존 인라인 키('archive-top' · 'archive-children')를
   * 그대로 유지한다. 폴더 생성·이동·권한 변경 뮤테이션의 prefix 무효화가
   * 좌측 트리와 폴더 피커에 함께 걸리도록.
   */
  // 권한 없는 폴더까지 받는 조회(좌측 트리 자물쇠 표시)는 끝에 'with-locked'를 붙여 따로 캐시한다
  folderTop: (includeInaccessible = false) =>
    includeInaccessible
      ? (['v2', 'archive-top', 'with-locked'] as const)
      : (['v2', 'archive-top'] as const),
  folderChildren: (
    folderId: number,
    topFolderId: number,
    permission?: string,
    includeInaccessible = false,
  ) =>
    [
      'v2',
      'archive-children',
      folderId,
      topFolderId,
      permission ?? 'all',
      ...(includeInaccessible ? (['with-locked'] as const) : []),
    ] as const,
  folderExpand: (folderId: number, topFolderId: number) =>
    ['v2', 'archive-expand', folderId, topFolderId] as const,
  // E1
  folderContentPaged: (
    folderId: number,
    pageNumber: number,
    pageSize: number,
  ) => ['v2', 'archive-content-paged', folderId, pageNumber, pageSize] as const,
  // E2 — 폴더 파일 목록
  archiveFiles: (folderId: number, pageNumber: number, pageSize: number) =>
    ['v2', 'archive-files', folderId, pageNumber, pageSize] as const,
  // N2
  fileDetail: (fileId: number) => ['v2', 'file-detail', fileId] as const,
  // 연관 파일 목록 (GET /files/{id}/assets)
  fileAssets: (fileId: number) => ['v2', 'file-assets', fileId] as const,
  // 휴지통 대표 파일의 연관 목록 — 같은 API지만 404를 null로 받으므로 키를 나눈다
  dumpFileAssets: (fileId: number) =>
    ['v2', 'dump-file-assets', fileId] as const,
  // 연관 파일 크기 합계 — 같은 API지만 실패를 null로 받고 합계만 들고 있으므로 키를 나눈다
  fileAssetsSize: (fileId: number) =>
    ['v2', 'file-assets-size', fileId] as const,
  // 메타데이터 폼 정의 (GET /metadata/schema)
  metadataSchema: () => ['v2', 'metadata-schema'] as const,
  /**
   * 선택지 관리 목록 (GET /metadata/enums) — schema의 options와 달리 서버 id가 있어
   * 수정·삭제가 가능하다. 필드명이 분류 간 중복될 수 있어 category를 키에 포함.
   */
  metadataEnums: (field: string, category: string | null) =>
    ['v2', 'metadata-enums', field, category ?? 'common'] as const,
  // 메타데이터 role 보유자 (GET /metadata/role-holders · 스웨거 미반영 제안 계약)
  metadataRoleHolders: () => ['v2', 'metadata-role-holders'] as const,
  // N10 — topFolderId 기준 (미지정 = 전체)
  searchFacets: (topFolderId?: number) =>
    ['v2', 'search-facets', topFolderId ?? 'all'] as const,
  // E3
  searchFiles: (params: GetFileSearchParams) =>
    ['v2', 'search-files', params] as const,
  /** N8 하이브리드 검색 — GET /files/search와 결과가 달라 키를 분리한다 */
  hybridSearch: (body: HybridSearchBody) =>
    ['v2', 'hybrid-search', body] as const,
  // E6 — 휴지통 파일 목록
  deletedFiles: (pageNumber: number, pageSize: number) =>
    ['v2', 'file-deleted', pageNumber, pageSize] as const,
  // N13/N15 ZIP 작업 폴링
  downloadJob: (jobId: string) => ['v2', 'download-job', jobId] as const,
  // N14
  cart: () => ['v2', 'cart'] as const,
};
